// src/pages/CustomerEditPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customersAPI } from '../services/api';

const CustomerEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    company_name: '',
    additional_address: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
    city: '',
    notes: '',
    reference_person: ''
  });

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        console.log('Versuche Kundendaten für Bearbeitung zu laden, ID:', id);
        const response = await customersAPI.getById(id);
        console.log('Kundendaten für Bearbeitung erfolgreich geladen:', response.data);
        setFormData(response.data.customer);
        setError('');
      } catch (err) {
        console.error('Fehler beim Laden der Kundendaten:', err);
        
        // Prüfen, ob es ein Authentifizierungsfehler ist
        if (err.response && err.response.status === 401) {
          // Manuell zur Login-Seite umleiten, wenn nicht authentifiziert
          console.log('Nicht authentifiziert, leite zur Login-Seite um');
          navigate('/login');
          return; // Wichtig: Return, um weitere Verarbeitung zu stoppen
        }
        
        setError('Fehler beim Laden der Kundendaten');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [id, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Versuche Kundendaten zu aktualisieren:', formData);
      await customersAPI.update(id, formData);
      console.log('Kundendaten erfolgreich aktualisiert');
      navigate(`/kunden/${id}`);
    } catch (err) {
      console.error('Fehler beim Aktualisieren des Kunden:', err);
      
      // Prüfen, ob es ein Authentifizierungsfehler ist
      if (err.response && err.response.status === 401) {
        // Manuell zur Login-Seite umleiten, wenn nicht authentifiziert
        console.log('Nicht authentifiziert, leite zur Login-Seite um');
        navigate('/login');
        return; // Wichtig: Return, um weitere Verarbeitung zu stoppen
      }
      
      setError('Fehler beim Aktualisieren des Kunden');
    }
  };

  if (loading) {
    return <div className="text-center py-6">Lade Kundendaten...</div>;
  }

  return (
    <div className="p-4 sm:p-0">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Kunde bearbeiten</h1>
        <button
          onClick={() => navigate(`/kunden/${id}`)}
          className="border border-gray-300 px-4 py-2 rounded text-gray-700 hover:bg-gray-100"
        >
          Abbrechen
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-md">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-gray-700 mb-2">Firma</label>
              <input
                type="text"
                name="company_name"
                value={formData.company_name || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Zusätzliche Adresszeile</label>
              <input
                type="text"
                name="additional_address"
                value={formData.additional_address || ''}
                onChange={handleChange}
                placeholder="z.B. Abteilung, c/o, Gebäude"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Vorname *</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name || ''}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Nachname *</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name || ''}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">E-Mail</label>
              <input
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Telefon</label>
              <input
                type="text"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Referenzperson</label>
              <input
                type="text"
                name="reference_person"
                value={formData.reference_person || ''}
                onChange={handleChange}
                placeholder="z.B. daniel.erni@firma.ch"
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Adresse</label>
              <input
                type="text"
                name="address"
                value={formData.address || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">PLZ</label>
              <input
                type="text"
                name="postal_code"
                value={formData.postal_code || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Ort</label>
              <input
                type="text"
                name="city"
                value={formData.city || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Notizen</label>
            <textarea
              name="notes"
              value={formData.notes || ''}
              onChange={handleChange}
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded"
            >
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerEditPage;
